import React from 'react';
import { CalendarDays, ChevronUp, ChevronDown } from 'lucide-react';
import { groupByEvent } from '../../lib/groupByEvent';

export interface StatusTone {
    label: string;
    className: string;
}

export interface TrackingColumn<T> {
    key: string;
    header: string;
    render: (row: T) => React.ReactNode;
    className?: string;
    /** Rend l'en-tête cliquable : un clic trie le tableau sur cette valeur (croissant, puis décroissant). */
    sortValue?: (row: T) => string | number;
}

export interface TrackingGroupInfo {
    key: string;
    label: string;
    date: string;
}

interface StatusTrackingTableProps<T> {
    columns: TrackingColumn<T>[];
    rows: T[];
    getRowKey: (row: T) => string;
    getStatus: (row: T) => StatusTone;
    renderActions?: (row: T) => React.ReactNode;
    onRowClick?: (row: T) => void;
    groupBy?: (row: T) => TrackingGroupInfo | null;
    emptyMessage: string;
    // Actions au niveau du groupe (ex: bouton "Télécharger le récap" des extras confirmés d'un événement) —
    // reçoit toutes les lignes de ce groupe pour que l'appelant décide lui-même s'il a quelque chose à
    // proposer (ex: seulement si au moins une ligne est confirmée). Demande d'Emeni (2026-08-22).
    renderGroupActions?: (rows: T[]) => React.ReactNode;
    /** Rend l'en-tête « Statut » cliquable pour trier, au même titre qu'une colonne ordinaire. */
    statusSortValue?: (row: T) => string;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};

// Table de suivi générique — remplace la navigation par onglets de Commandes/Extras par un tableau continu
// avec une colonne statut (pastille) et un filtre (StatusFilterChips) au-dessus. Le regroupement optionnel
// (groupBy) réutilise groupByEvent pour préserver le regroupement par privatisation côté Extras : une ligne
// d'en-tête de groupe (colSpan) est insérée avant chaque groupe, les lignes sans groupe suivent sans en-tête.
export function StatusTrackingTable<T>({ columns, rows, getRowKey, getStatus, renderActions, onRowClick, groupBy, emptyMessage, renderGroupActions, statusSortValue }: StatusTrackingTableProps<T>) {
    // Tri au clic sur un en-tête de colonne — remplace les pastilles de filtre séparées sur les écrans qui
    // l'activent (demande d'Emeni, 2026-09-01 : « on s'y perd trop d'information », préférer trier depuis le
    // tableau lui-même). `sortKey` vaut 'status' pour la colonne Statut, sinon la clé d'une colonne ordinaire.
    const [sortKey, setSortKey] = React.useState<string | null>(null);
    const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

    if (rows.length === 0) {
        return <p className="text-xs text-content-muted">{emptyMessage}</p>;
    }

    const colCount = columns.length + 1 + (renderActions ? 1 : 0);

    const trierPar = (key: string) => {
        if (sortKey === key) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
        setSortKey(key);
        setSortDir('asc');
    };

    const sortedRows = React.useMemo(() => {
        if (!sortKey) return rows;
        const valeur = sortKey === 'status'
            ? statusSortValue
            : columns.find((c) => c.key === sortKey)?.sortValue;
        if (!valeur) return rows;
        const copie = [...rows];
        copie.sort((a, b) => {
            const va = valeur(a);
            const vb = valeur(b);
            const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'fr');
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return copie;
    }, [rows, sortKey, sortDir, columns, statusSortValue]);

    const renderRow = (row: T) => (
        <tr key={getRowKey(row)} onClick={() => onRowClick?.(row)}
            className={onRowClick ? 'cursor-pointer hover:bg-surface-soft transition-colors' : ''}>
            {columns.map((col) => (
                <td key={col.key} className={`px-3 py-2.5 align-top text-sm text-content ${col.className || ''}`}>{col.render(row)}</td>
            ))}
            <td className="px-3 py-2.5 align-top">
                {(() => {
                    const status = getStatus(row);
                    return <span className={`inline-flex text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest whitespace-nowrap ${status.className}`}>{status.label}</span>;
                })()}
            </td>
            {renderActions && (
                <td className="px-3 py-2.5 align-top" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 flex-wrap">{renderActions(row)}</div>
                </td>
            )}
        </tr>
    );

    const groupHeaderRow = (key: string, label: string, date: string, items: T[]) => (
        <tr key={`group-${key}`} className="bg-brand-500/10">
            <td colSpan={colCount} className="px-3 py-1.5">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-brand-600 dark:text-brand-400 uppercase tracking-widest">{label || 'Sans privatisation'}</span>
                        {date && <span className="text-[10px] text-content-muted flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(date)}</span>}
                    </div>
                    {renderGroupActions?.(items)}
                </div>
            </td>
        </tr>
    );

    let body: React.ReactNode;
    if (groupBy) {
        const { groups, ungrouped } = groupByEvent(sortedRows, groupBy);
        body = (
            <>
                {groups.map((g) => (
                    <React.Fragment key={g.key}>
                        {groupHeaderRow(g.key, g.label, g.date, g.items)}
                        {g.items.map(renderRow)}
                    </React.Fragment>
                ))}
                {ungrouped.map(renderRow)}
            </>
        );
    } else {
        body = sortedRows.map(renderRow);
    }

    const enTete = (key: string, label: string, sortable: boolean, className?: string) => (
        <th key={key} className={`px-3 py-2 text-left text-[10px] font-black text-content-muted uppercase tracking-widest ${sortable ? 'cursor-pointer select-none hover:text-content' : ''} ${className || ''}`}
            onClick={sortable ? () => trierPar(key) : undefined}>
            <span className="inline-flex items-center gap-1">
                {label}
                {sortable && sortKey === key && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
            </span>
        </th>
    );

    return (
        <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full border-collapse">
                <thead>
                    <tr className="bg-surface-soft border-b border-border">
                        {columns.map((col) => enTete(col.key, col.header, !!col.sortValue, col.className))}
                        {enTete('status', 'Statut', !!statusSortValue)}
                        {renderActions && <th className="px-3 py-2 text-left text-[10px] font-black text-content-muted uppercase tracking-widest">Actions</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">{body}</tbody>
            </table>
        </div>
    );
}
